import {
  DATE_FORMATS,
  FormFieldType,
  FORM_FIELDS_MAP,
  isOnServer,
  PARTNER,
  PARTNER_DOMAIN_NAME,
  TRIPMONEY_DOMAIN_NAME,
  UTM_PARAMS,
} from "@tm/isomorphic-utils";
import { getHostName } from "../../utils";
import { formatDate } from "./dateUtils";

export const setURLQueryParams = (
  params: Record<string, string | number | boolean> = {}
) => {
  const usp = new URLSearchParams();
  Object.keys(params).forEach((e) => {
    if (params[e]) {
      usp.set(e, params[e].toString());
    }
  });
  return usp.toString();
};

export const persistUtmParams = (queryParam: URLSearchParams) => {
  const utm_source = queryParam.get(UTM_PARAMS.UTM_SOURCE);
  const utm_medium = queryParam.get(UTM_PARAMS.UTM_MEDIUM);
  const utm_campaign = queryParam.get(UTM_PARAMS.UTM_CAMPAIGN);

  if (utm_source) {
    sessionStorage.setItem(UTM_PARAMS.UTM_SOURCE, utm_source);
  }

  if (utm_medium) {
    sessionStorage.setItem(UTM_PARAMS.UTM_MEDIUM, utm_medium);
  }

  if (utm_campaign) {
    sessionStorage.setItem(UTM_PARAMS.UTM_INS_CAMPAIGN, utm_campaign);
  }
};

export const replaceHistory = (param: string, value: string) => {
  if (typeof window !== "undefined") {
    const baseUrl = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    value?.length > 0 ? searchParams.set(param, value) : null;
    const url = `${baseUrl}${
      searchParams.toString().length > 0 ? "?" + searchParams : ""
    }`;
    window.history.replaceState(
      { ...window.history.state, as: url, url: url },
      "",
      url
    );
  }
};

export const replaceHistoryRemoveParams = (param: string) => {
  const baseUrl = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.delete(param);

  const url = `${baseUrl}${
    searchParams.toString().length > 0 ? "?" + searchParams : ""
  }`;
  window.history.replaceState(
    { ...window.history.state, as: url, url: url },
    "",
    url
  );
};

export const getCookieByName = (cookieName: string) => {
  const cookie: Record<string, string> = {};
  document.cookie.split(";").forEach((el) => {
    const [key, value] = el.split("=");
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

export const getFormFieldErrorMsg = (
  field: FormFieldType,
  errorMsg?: string
) => {
  if (errorMsg) {
    return errorMsg;
  } else {
    const fieldName = FORM_FIELDS_MAP[field] || field;
    return `Please enter ${fieldName}`;
  }
};

export const isDobValid = (value: string | Date, regex?: string) => {
  const formattedDate = formatDate(value, DATE_FORMATS["DD/MM/YYYY"]);
  const isValidDate = new RegExp(regex || "").test(formattedDate);
  return isValidDate;
};

export const getPartnerOnClientSide = () => {
  const hostname = getHostName() || "";
  if (hostname.includes(PARTNER_DOMAIN_NAME.MYPARTNER)) {
    return PARTNER.MYPARTNER;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.MMT)) {
    return PARTNER.MMT;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.GI)) {
    return PARTNER.GI;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.TRIPMONEY)) {
    return PARTNER.TRIPMONEY;
  } else {
    console.error(
      `Error detecting partner. Unsupported domain hostname - ${hostname}`
    );
    return PARTNER.TRIPMONEY;
  }
};

export const isPartnerMMT = () => {
  return getPartnerOnClientSide() === PARTNER.MMT;
};

export const isPartnerGI = () => {
  return getPartnerOnClientSide() === PARTNER.GI;
};

export const isMyPartner = () => {
  return getPartnerOnClientSide() === PARTNER.MYPARTNER;
};

export const isFieldValid = (regex: string, updatedField: string): boolean => {
  try {
    const validator = new RegExp(regex || "");
    return validator?.test(updatedField);
  } catch (error) {
    return false;
  }
};

export const isHostMakemytrip = () => {
  if (isOnServer()) return false;
  const url = new URL(window.location.href);
  return TRIPMONEY_DOMAIN_NAME.MMT.includes(url.hostname);
};

export const debounce = <T extends any[]>(
  func: (...args: T) => void,
  delay: number
) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: T) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const generateSourceSetImageUrl = (
  cdnUrlWidths: Array<number>,
  imgUrl: string,
  width: number
) => {
  let imgWidths = cdnUrlWidths;
  if (!imgWidths || !Array.isArray(imgWidths) || imgWidths.length === 0)
    imgWidths = [width, width * 2, width * 3];

  return imgWidths
    .map((width) => `${imgUrl}?im=Resize=(${width}) ${width}w`)
    .join(",");
};

export const isObject = (item: any) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

export function deepMergeObjects(target: any, ...sources: any[]): any {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!target[key]) target[key] = {};
        target[key] = deepMergeObjects(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    });
  }

  return deepMergeObjects(target, ...sources);
}

export type Region = "normal" | "WW" | "EU" | "SEA" | "WWEUC";

export type HeightData = {
  [key: number]: {
    [key in Region]: number[];
  };
};

//TODO: Check for a better way to handle the Dynamic size of cells in react-window
export const interpolateRowHeight = (
  width: number,
  region: Region,
  expansionLevel = 0
) => {
  const heightData: HeightData = {
    360: {
      normal: [57],
      WW: [57, 159],
      EU: [57, 183, 399],
      SEA: [57, 207, 207],
      WWEUC: [65.5, 215.5, 215],
    },
    370: {
      normal: [57],
      WW: [57, 159],
      SEA: [57, 183, 183],
      EU: [57, 183, 375],
      WWEUC: [65.5, 215.5, 191.5],
    },
    315: {
      normal: [57],
      WW: [57, 183],
      SEA: [57, 207, 231],
      EU: [57, 207, 471],
      WWEUC: [65.5, 215.5, 215.5],
    },
    354: {
      normal: [57],
      WW: [57, 159],
      SEA: [57, 207, 207],
      EU: [57, 183, 399],
      WWEUC: [65.5, 215.5, 215.5],
    },
    300: {
      normal: [57],
      WW: [57, 183],
      SEA: [57, 231, 231],
      EU: [57, 207, 495],
      WWEUC: [65.5, 239.5, 215.5],
    },
    708: {
      normal: [57],
      WW: [57, 135],
      SEA: [57, 159, 159],
      EU: [57, 159, 255],
      WWEUC: [65.5, 167.5, 143.5],
    },
    480: {
      normal: [57],
      WW: [57, 135],
      SEA: [57, 183, 159],
      EU: [57, 183, 303],
      WWEUC: [65.5, 215.5, 191.5],
    },
  };

  const availableWidths = Object.keys(heightData)
    .map(Number)
    .sort((a, b) => a - b);

  const findNearestWidths = (targetWidth: number) => {
    let lowerWidth = availableWidths[0];
    let upperWidth = availableWidths[availableWidths.length - 1];

    for (let i = 0; i < availableWidths.length - 1; i++) {
      if (
        availableWidths[i] <= targetWidth &&
        availableWidths[i + 1] >= targetWidth
      ) {
        lowerWidth = availableWidths[i];
        upperWidth = availableWidths[i + 1];
        break;
      }
    }
    return [lowerWidth, upperWidth];
  };

  const [lowerWidth, upperWidth] = findNearestWidths(width);

  const lowerHeights = heightData?.[lowerWidth]?.[region] || [57];
  const upperHeights = heightData?.[upperWidth]?.[region] || [57];

  const lowerHeight =
    lowerHeights[Math.min(expansionLevel, lowerHeights.length - 1)];
  const upperHeight =
    upperHeights[Math.min(expansionLevel, upperHeights.length - 1)];

  const interpolatedHeight =
    lowerHeight +
    ((upperHeight - lowerHeight) * (width - lowerWidth)) /
      (upperWidth - lowerWidth);

  return interpolatedHeight;
};
